import React from 'react';

import { CollapsibleCard } from 'components/DesignSystems/Collapse/CollapsibleCard';
import Loader from 'components/Loader/Loader';
import SitrepCardHeader from 'components/Reports/SitrepCardHeader';
import useOpenReportSelection from 'components/Reports/hooks/useOpenReportSelection';
import useSitrepCardContentData from 'components/Reports/hooks/useSitrepCardContentData';
import type {
  GetSitrepsResponse,
  SitrepCardSurface,
} from 'components/Reports/reports.types';

type SitrepCardContentData = ReturnType<typeof useSitrepCardContentData>;

/**
 * Props accepted by {@link SitrepCardShell}.
 *
 * The shell handles the outermost card chrome (`CollapsibleCard`, header,
 * `openReport` search-param tracking, loading state). The content area is
 * rendered by the caller via `children`.
 *
 * Callers provide the already-fetched shell data they are using for the rest of
 * the card, so the shell stays presentation-focused.
 */
type SitrepCardShellProps = React.PropsWithChildren<{
  sitrep: GetSitrepsResponse;
  surface: SitrepCardSurface;
  headerRight?: React.ReactNode;
  /** Called when the card collapses — surfaces can reset edit state here. */
  onCollapse?: () => void;
  cardData: SitrepCardContentData;
}>;

const SitrepCardShell: React.FC<SitrepCardShellProps> = ({
  sitrep,
  surface,
  headerRight,
  onCollapse,
  cardData,
  children,
}) => {
  const { hasOpenReportSelection, isOpenReportMatch, setOpenReport } =
    useOpenReportSelection(sitrep.id, sitrep.item_id);

  const { sectionsLoading, aorName, incidentName } = cardData;

  return (
    <div className="reports-card">
      <CollapsibleCard
        sectionKey={`sitrep-${sitrep.item_id}`}
        headerLeft={
          <SitrepCardHeader
            sitrep={sitrep}
            incidentName={incidentName}
            aorName={aorName}
            surface={surface}
          />
        }
        headerRight={headerRight}
        headerClassName="p-3"
        contentClassName="p-3 pt-0"
        defaultCollapsed={hasOpenReportSelection && !isOpenReportMatch}
        onStateChange={(isExpanded) => {
          if (isExpanded && !isOpenReportMatch) {
            setOpenReport(sitrep.item_id);
          }
        }}
        onCollapse={() => {
          setOpenReport('');
          onCollapse?.();
        }}
      >
        {sectionsLoading ? <Loader /> : children}
      </CollapsibleCard>
    </div>
  );
};

export default SitrepCardShell;
